/**
 * Menu de construction: catalogue des modeles, couleurs personnalisables, puis
 * barre de placement quand le fantome est dans la scene.
 *
 * Le menu ne pose rien lui-meme: il annonce le choix (`onChoix`) et le jeu
 * s'occupe du fantome et de la validation du terrain, qui redonne son verdict
 * par `majPlacement`.
 */
import type { Inventaire } from '../../shared/types/items.ts'
import type { BatimentKind } from '../../shared/types/monde.ts'
import { BATIMENTS, CATEGORIES_BATIMENT } from '../../shared/data/batiments.ts'
import { RECETTES_OUTIL } from '../../shared/data/recettes.ts'
import { VEHICULES, VEHICULES_MENU } from '../../shared/data/vehicules.ts'
import type { VehiculeKind } from '../../shared/types/monde.ts'
import { peutPayer, quantite } from '../../shared/sim/inventaire.ts'
import { NOMBRE_ENCLOS } from '../../shared/sim/zoo.ts'
import { CAPACITE_ENCLOS } from '../../shared/data/zoo.ts'
import { journal } from '../log/journal.ts'
import { depiler, empiler, type Couche } from './retour.ts'
import {
  Panneau,
  afficherCout,
  bouton,
  clicSonore,
  depuisHex,
  el,
  grille,
  hex,
  refusSonore,
  section,
} from './commun.ts'

/** Couleurs de depart d'un batiment neuf. */
const COULEURS_DEPART = { murs: 0xd9cbb2, toit: 0xe23b3b, porte: 0x8b5a2b }

/**
 * Le mode d'emploi du zoo, dans la rubrique du village.
 *
 * « There's no zoo », ont dit les joueurs. La rubrique existait pourtant — mais
 * elle ne contenait que des clotures, des portails, des haies et des murets,
 * parce qu'un enclos ne se POSAIT pas: il se DESSINAIT. Rien ne le disait,
 * alors on ouvrait la rubrique, on n'y trouvait pas de « zoo », et on en
 * concluait qu'il n'existait pas.
 *
 * Le zoo est desormais un BATIMENT, avec ses trois enclos deja dessines, et ces
 * quatre lignes ne decrivent plus une methode de construction: elles disent ce
 * qu'on y fait. Les phrases reprennent mot pour mot les refus de
 * `shared/sim/zoo.ts`, pour que le joueur reconnaisse une phrase deja lue.
 */
function modeEmploiZoo(): HTMLElement {
  const boite = el('div', 'mode-emploi')
  boite.append(el('p', 'mode-emploi__titre', 'Le zoo se pose d’un seul geste, enclos compris.'))
  const etapes = el('ol', 'mode-emploi__etapes')
  for (const texte of [
    `Pose le zoo sur un terrain plat et dégagé : il est grand (${NOMBRE_ENCLOS} enclos et une allée), et il lui faut de la place.`,
    'Apprivoise un animal en le nourrissant trois fois, à des moments différents. Il finit par te suivre.',
    'Amène-le à l’intérieur d’un enclos et touche-le : il s’y installe. L’abreuvoir, le râtelier et l’abri poussent tout seuls.',
    `Chaque enclos loge ${CAPACITE_ENCLOS} pensionnaires. Touche un animal installé pour le rendre à la nature : il reste ton ami.`,
  ]) {
    etapes.append(el('li', '', texte))
  }
  boite.append(etapes)
  return boite
}

const NOMS_COULEUR: Readonly<Record<'murs' | 'toit' | 'porte', string>> = {
  murs: 'Murs',
  toit: 'Toit',
  porte: 'Porte',
}

export class MenuConstruction extends Panneau {
  /** Un modele a ete choisi: le jeu passe en mode placement. */
  onChoix: ((kind: BatimentKind) => void) | null = null
  /** Les couleurs ont change avant la pose. */
  onCouleurs: ((c: { murs: number; toit: number; porte: number }) => void) | null = null
  /** Le joueur renonce (fermeture du menu ou annulation du placement). */
  onAnnuler: (() => void) | null = null

  /** L'utilisateur choisit un vehicule a poser. */
  onVehicule: ((kind: VehiculeKind) => void) | null = null

  /** L'utilisateur fabrique un outil (identifiant de recette). */
  onOutil: ((idRecette: string) => void) | null = null

  /** L'utilisateur bascule le mode demolition. */
  onDemolition: ((actif: boolean) => void) | null = null

  private demolitionActive = false
  /** Bouton "Tourner" de la barre de placement. */
  onTourner: (() => void) | null = null
  /** Bouton "Poser" de la barre de placement. */
  onPoser: (() => void) | null = null

  private readonly barre: HTMLDivElement
  private readonly etatPlacement: HTMLDivElement
  private readonly btnPoser: HTMLButtonElement
  private couleurs = { ...COULEURS_DEPART }
  private inv: Inventaire = {}
  private placementValide = false
  /** Vrai quand la fermeture vient d'un choix de modele: pas une annulation. */
  private fermetureParChoix = false
  /** « Retour » pendant le placement l'annule, comme le bouton ✕ Annuler. */
  private readonly coucheBarre: Couche = {
    nom: 'placement',
    fermer: () => {
      journal.input('construire', 'placement: annule par la touche retour')
      this.onAnnuler?.()
    },
  }

  constructor(parent: HTMLElement) {
    super(parent, 'construire', 'Construire', '🔨', 'panneau-fond--construire')

    this.barre = el('div', 'placement')
    this.barre.hidden = true
    this.etatPlacement = el('div', 'placement__etat', 'Touche « Poser » pour bâtir')
    const actions = el('div', 'placement__actions')
    actions.append(
      bouton('🔄 Tourner', () => {
        journal.input('construire', 'placement: tourner')
        this.onTourner?.()
      }, 'bouton--sombre'),
    )
    this.btnPoser = bouton('✔ Poser', () => this.poser(), 'bouton--vert')
    actions.append(this.btnPoser)
    actions.append(
      bouton('✕ Annuler', () => {
        journal.input('construire', 'placement: annule')
        this.fermerPlacement()
        this.onAnnuler?.()
      }, 'bouton--sombre'),
    )
    this.barre.append(this.etatPlacement, actions)
    parent.append(this.barre)
  }

  // -------------------------------------------------------------- ouverture

  ouvrir(inv: Inventaire): void {
    this.inv = inv
    this.fermetureParChoix = false
    // Ouvrir le menu QUITTE la demolition (le jeu repasse en construction):
    // sans cette remise a zero, le bouton gardait l'etat d'avant et il fallait
    // le toucher deux fois — « Mode démolition terminé » au premier appui.
    this.demolitionActive = false
    this.fermerPlacement()
    this.construire()
    this.afficher('catalogue')
  }

  /** Reconstruit la liste si le panneau est deja ouvert (inventaire change). */
  rafraichir(inv: Inventaire): void {
    this.inv = inv
    if (this.ouvert) this.construire()
  }

  private construire(): void {
    this.viderCorps()
    this.viderPied()

    this.corps.append(this.sectionOutils())

    // Le catalogue compte une cinquantaine de modeles: on les presente par
    // rubrique, sinon la liste est illisible. Les rubriques viennent des donnees,
    // donc ajouter un modele suffit a le faire apparaitre ici.
    for (const cat of CATEGORIES_BATIMENT) {
      const bloc = section(cat.nom, cat.icone)
      // La rubrique du zoo s'explique AVANT sa grille: c'est en la lisant qu'on
      // comprend pourquoi elle ne contient que des clotures.
      if (cat.id === 'village') bloc.append(modeEmploiZoo())
      bloc.append(this.grilleModeles(cat.modeles))
      this.corps.append(bloc)
    }

    const couleurs = section('Couleurs', '🎨')
    const ligne = el('div', 'couleurs-batiment')
    for (const cle of ['murs', 'toit', 'porte'] as const) {
      const enveloppe = el('label', 'couleur-champ')
      enveloppe.append(el('span', 'couleur-champ__nom', NOMS_COULEUR[cle]))
      const entree = el('input', 'couleur-champ__entree')
      entree.type = 'color'
      entree.value = hex(this.couleurs[cle])
      entree.addEventListener('input', () => {
        const suite = { ...this.couleurs }
        suite[cle] = depuisHex(entree.value)
        this.couleurs = suite
        journal.input('construire', `couleur ${cle}=${entree.value}`)
        this.onCouleurs?.({ ...this.couleurs })
      })
      enveloppe.append(entree)
      ligne.append(enveloppe)
    }
    couleurs.append(ligne)
    couleurs.append(
      el(
        'p',
        'note',
        'Les couleurs s’appliquent au prochain bâtiment posé. Chaque modèle n’utilise que les couleurs qui lui correspondent.',
      ),
    )
    this.corps.append(couleurs)

    // Vehicules: ils se construisent comme le reste, mais ils se pilotent.
    const vehicules = section('Véhicules', '🚗')
    for (const kind of VEHICULES_MENU) {
      const def = VEHICULES[kind]
      const carte = el('button', 'carte carte--batiment')
      carte.append(el('span', 'carte__icone', def.icone))
      const infos = el('div', 'carte__infos')
      infos.append(el('span', 'carte__nom', def.nom))
      infos.append(afficherCout(def.cout, this.inv))
      carte.append(infos)
      carte.disabled = !peutPayer(this.inv, def.cout)
      carte.addEventListener('click', () => {
        journal.input('construire', `vehicule=${kind}`)
        /*
         * `fermetureParChoix` AVANT `fermer()`, comme le choix d'un modele et
         * comme la demolition. C'est la TROISIEME fois que ce piege est paye:
         * sans lui, `surFermeture` prend la fermeture pour un abandon, appelle
         * `onAnnuler`, et le mode qu'on vient d'activer est annule dans le
         * meme geste — `vehiculeEnPose` repasse a null et la barre de
         * placement ne s'ouvre jamais.
         *
         * Ca ne se voyait pas toujours, ce qui est pire: un second clic
         * rapide tombait sur un panneau DEJA ferme, donc sans `onAnnuler`, et
         * la pose marchait. Le journal d'Amelia du 18 aout montre exactement
         * ca — deux « vehicule choisi: avion » a 128 ms d'ecart, et seul le
         * second ouvre le placement.
         */
        this.fermetureParChoix = true
        this.onVehicule?.(kind)
        this.fermer()
      })
      vehicules.append(carte)
    }
    this.corps.append(vehicules)

    // Demolition: c'est ici qu'on la propose, parce que c'est le pendant de la
    // construction. On ne peut demolir que ses PROPRES constructions.
    const demolir = section('Démolir', '🧨')
    demolir.append(
      el(
        'div',
        'pied__note',
        'Tu peux démolir ce que TU as construit et récupérer la moitié des ressources. Les constructions des autres ne peuvent pas être touchées.',
      ),
    )
    demolir.append(
      bouton(
        '🧨 Mode démolition',
        () => {
          this.demolitionActive = !this.demolitionActive
          journal.input('construire', `mode demolition ${this.demolitionActive ? 'actif' : 'inactif'}`)
          /*
           * `fermetureParChoix` AVANT `fermer()`, comme pour le choix d'un modele.
           * Sans ca, la fermeture etait prise pour un abandon: `surFermeture`
           * appelait `onAnnuler`, qui quittait le mode construction et remettait
           * le mode a 'jeu'. On activait donc la demolition et on la desactivait
           * dans le meme geste — le mode s'annoncait dans le journal et le bandeau,
           * puis les taps repartaient en marche normale. Demolir etait impossible.
           */
          this.fermetureParChoix = true
          this.onDemolition?.(this.demolitionActive)
          this.fermer()
        },
        'bouton--sombre',
      ),
    )
    this.corps.append(demolir)

    this.pied.append(el('span', 'pied__note', 'Choisis un modèle, place-le en touchant le sol, puis touche « Poser ».'))
  }

  /**
   * Les outils, en tete du menu.
   *
   * EN TETE, et pas au fond avec les vehicules: c'est ici que le proprietaire est
   * venu chercher sa canne a peche sans la trouver. Un outil ne se pose pas dans
   * le monde, il tombe dans le sac — le panneau reste donc ouvert apres la
   * fabrication, et la carte se contente de dire que c'est fait.
   */
  private sectionOutils(): HTMLElement {
    const s = section('Outils', '🧰')
    for (const r of RECETTES_OUTIL) {
      const possede = quantite(this.inv, r.produit) > 0
      const possible = peutPayer(this.inv, r.cout)
      const carte = el('button', 'carte carte--batiment')
      carte.type = 'button'
      if (!possible && !possede) carte.classList.add('carte--bloque')
      carte.append(el('span', 'carte__icone', r.icone))
      const infos = el('div', 'carte__infos')
      infos.append(el('span', 'carte__nom', r.nom))
      if (possede) {
        infos.append(el('span', 'carte__detail', 'Déjà dans ton sac · touche l’eau pour pêcher'))
      } else {
        infos.append(el('span', 'carte__detail', 'Pêche de loin, sans barque'))
        infos.append(afficherCout(r.cout, this.inv))
      }
      carte.append(infos)
      carte.addEventListener('click', () => {
        if (possede) {
          journal.input('construire', `outil ${r.id} deja possede`)
          return
        }
        if (!possible) {
          refusSonore()
          journal.input('construire', `outil ${r.id} refuse: ressources manquantes`)
          carte.classList.remove('carte--refus')
          void carte.offsetWidth
          carte.classList.add('carte--refus')
          return
        }
        clicSonore()
        journal.input('construire', `outil=${r.id}`)
        this.onOutil?.(r.id)
      })
      s.append(carte)
    }
    return s
  }

  private grilleModeles(kinds: readonly BatimentKind[]): HTMLElement {
    const g = grille('grille--batiments')
    for (const kind of kinds) {
      const def = BATIMENTS[kind]
      const possible = peutPayer(this.inv, def.cout)
      const carte = el('button', 'carte carte--batiment')
      carte.type = 'button'
      if (!possible) carte.classList.add('carte--bloque')
      carte.append(el('span', 'carte__icone', def.icone))
      const infos = el('div', 'carte__infos')
      infos.append(el('span', 'carte__nom', def.nom))
      infos.append(
        el('span', 'carte__detail', `${def.taille.l} × ${def.taille.p} m${def.habitable ? ' · on entre' : ''}`),
      )
      infos.append(afficherCout(def.cout, this.inv))
      carte.append(infos)
      carte.addEventListener('click', () => {
        if (!possible) {
          refusSonore()
          journal.input('construire', `modele ${kind} refuse: ressources manquantes`)
          carte.classList.remove('carte--refus')
          void carte.offsetWidth
          carte.classList.add('carte--refus')
          return
        }
        clicSonore()
        journal.input('construire', `modele=${kind}`, { couleurs: this.couleurs })
        this.fermetureParChoix = true
        this.fermer()
        this.onCouleurs?.({ ...this.couleurs })
        this.onChoix?.(kind)
      })
      g.append(carte)
    }
    return g
  }

  protected override surFermeture(): void {
    if (this.fermetureParChoix) {
      this.fermetureParChoix = false
      return
    }
    this.onAnnuler?.()
  }

  // -------------------------------------------------------------- placement

  /** Etat du fantome de placement. Affiche la barre du bas si besoin. */
  majPlacement(valide: boolean, raison?: string): void {
    this.placementValide = valide
    if (this.barre.hidden) {
      this.barre.hidden = false
      requestAnimationFrame(() => this.barre.classList.add('placement--ouvert'))
      empiler(this.coucheBarre)
      journal.input('construire', 'mode placement actif')
    }
    this.barre.classList.toggle('placement--refus', !valide)
    this.etatPlacement.textContent = valide ? '✔ Ici, c’est bon : touche « Poser »' : `✕ ${raison ?? 'Impossible ici'}`
    this.btnPoser.classList.toggle('bouton--vert', valide)
    this.btnPoser.setAttribute('aria-disabled', valide ? 'false' : 'true')
  }

  /** Cache la barre de placement. */
  fermerPlacement(): void {
    if (this.barre.hidden) return
    depiler(this.coucheBarre)
    this.barre.classList.remove('placement--ouvert')
    this.barre.hidden = true
    journal.input('construire', 'mode placement termine')
  }

  private poser(): void {
    if (!this.placementValide) {
      refusSonore()
      journal.input('construire', 'pose refusee: emplacement invalide')
      return
    }
    journal.input('construire', 'pose confirmee')
    this.onPoser?.()
  }

  override detruire(): void {
    this.barre.remove()
    this.onChoix = null
    this.onCouleurs = null
    this.onAnnuler = null
    this.onDemolition = null
    this.onVehicule = null
    this.onOutil = null
    this.onTourner = null
    this.onPoser = null
    super.detruire()
  }
}
